import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

const Home = () => {
  const featuredServices = [
    {
      id: 'biorepeel',
      name: 'BioRePeel',
      description: 'Advanced bio-stimulating peel treatment for skin renewal.',
      image: 'https://customer-assets.emergentagent.com/job_spa-scheduler-7/artifacts/opynjpzr_BioRePeel-Image.jpg'
    },
    {
      id: 'signature-facial',
      name: 'Signature Facial',
      description: 'Our signature relaxing facial treatment with customized skincare.',
      image: 'https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg'
    },
    {
      id: 'sugaring',
      name: 'Hair Removal',
      description: 'Natural sugar wax hair removal technique for smooth skin.',
      image: 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/2zjvfr3a_curated-lifestyle-2vwjeqXZevk-unsplash.jpg'
    },
    {
      id: 'aromatherapy-massage',
      name: 'Aromatherapy Massage',
      description: 'A soothing, full-body massage using essential oils to calm the mind and ease tension.',
      image: 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/oz4stkvh_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2001_25_47%20PM.png'
    }
  ];

  const getBookingUrl = (serviceId) => {
    const urls = {
      'biorepeel': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22368321&share=true&pId=2492446',
      'signature-facial': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22173011&share=true&pId=2492446',
      'sugaring': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=4526724&oiid=sv%3A24636434&share=true&pId=2492446',
      'aromatherapy-massage': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=5115297&oiid=sv%3A24776685&share=true&pId=2492446'
    };
    return urls[serviceId] || 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446';
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const totalSlides = featuredServices.length;

  useEffect(() => {
    const updateVisibleCards = () => {
      const w = window.innerWidth;
      if (w <= TABLET_BREAKPOINT) {
        setVisibleCards(1);
      } else if (w <= DESKTOP_BREAKPOINT) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };
    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  // Reset index if it exceeds bounds after resize
  useEffect(() => {
    if (currentIndex > totalSlides - 1) setCurrentIndex(0);
  }, [visibleCards, currentIndex, totalSlides]);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  return (
    <div className="home">
      <SEOHead 
        title="Sereno Spa | Luxury Day Spa & Wellness Center in Livingston Manor, NY"
        description="Experience ultimate relaxation at Sereno Spa in Livingston Manor, NY. Professional facials, hair removal, lashes & brows, chemical peels, massage, and wellness treatments. Book your spa day today!"
        canonical="https://serenodayspa.com/"
        ogTitle="Sereno Spa - Luxury Day Spa in Livingston Manor, NY"
        ogDescription="Discover serenity for your skin at our luxury spa. Advanced facials, wellness treatments, and professional skincare services in the heart of New York's Catskill Mountains."
      />
      {/* Hero Section */}
      <section className="hero-home">
        <div className="hero-image-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/2mm0tu6q_usen-parmanov-yyOA_rgdEZ0-unsplash-2.jpg" 
            alt="Sereno Spa - Luxury Wellness Experience"
            className="hero-image"
          />
          <div className="hero-overlay">
            <div className="hero-content">
              <h1 className="hero-title">Welcome to Sereno Spa</h1>
              <p className="hero-subtitle">Serenity for Your Skin</p>
              <p className="hero-description">
                Discover ultimate relaxation and rejuvenation at our luxury spa. 
                From advanced facials to wellness treatments, we provide a sanctuary for your well-being.
              </p>
              <div className="hero-actions">
                <a href="https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446" target="_blank" rel="noopener noreferrer" className="btn-primary">Book Now</a>
                <Link to="/services" className="btn-secondary">Explore Services</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services Carousel */}
      <section className="section featured-services">
        <div className="container">
          <h2 className="section-title">Featured Services</h2>
          <p className="section-subtitle">
            Experience our most popular treatments designed to restore and revitalize
          </p>
          <div className="carousel-wrapper" data-testid="featured-services-carousel">
            <button 
              className="carousel-arrow carousel-arrow-left" 
              onClick={prevSlide}
              aria-label="Previous service"
              data-testid="carousel-prev"
            >
              &#8249;
            </button>
            <div className="carousel-viewport">
              <div 
                className="carousel-track"
                style={{ transform: `translateX(-${currentIndex * (100 / visibleCards)}%)` }}
              >
                {/* Render all cards plus clones for seamless wrapping */}
                {[...featuredServices, ...featuredServices.slice(0, 3)].map((service, idx) => (
                  <div key={`${service.id}-${idx}`} className="carousel-slide">
                    <div className="service-card">
                      <img 
                        src={service.image} 
                        alt={service.name}
                        className="service-image"
                      />
                      <div className="service-content">
                        <h3>{service.name}</h3>
                        <p>{service.description}</p>
                        <a 
                          href={getBookingUrl(service.id)}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-primary"
                          data-testid={`book-now-${service.id}`}
                        >
                          Book Now
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button 
              className="carousel-arrow carousel-arrow-right" 
              onClick={nextSlide}
              aria-label="Next service"
              data-testid="carousel-next"
            >
              &#8250;
            </button>
          </div>
          <div className="carousel-dots" data-testid="carousel-dots">
            {featuredServices.map((service, idx) => (
              <button 
                key={service.id}
                className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                data-testid={`carousel-dot-${idx}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-choose-us">
        <div className="container">
          <h2 className="section-title">Why Choose Sereno Spa</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">✨</div>
              <h3>Expert Providers</h3>
              <p>Our licensed professionals provide personalized treatments using the latest techniques and premium products.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🌿</div>
              <h3>Results Driven Products</h3>
              <p>We use only trusted, effective products to ensure safe, effective treatments that deliver real results for all skin types.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🪷</div>
              <h3>Serene Environment</h3>
              <p>Relax in our beautifully designed spa with state-of-the-art facilities and a tranquil, peaceful atmosphere.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📅</div>
              <h3>Convenient Scheduling</h3>
              <p>Easy online booking with real-time availability. Book your perfect treatment time that fits your schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience Ultimate Relaxation?</h2>
            <p>Book your appointment today and let us help you achieve your wellness goals.</p>
            <div className="cta-actions">
              <a href="https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446" target="_blank" rel="noopener noreferrer" className="btn-primary">Book Now</a>
              <span className="cta-phone">Or call us at 845-439-1084</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;